'use client';
import { featureFlagsSelectors } from '@/lib/store/feature-flags.store';

import { Route } from '@/components/common/navigation/types';
import { useUser } from '@/lib/hooks/useUser';
import { cn } from '@/lib/utils';
import { I18nProviderClient, useCurrentLocale } from '@/locales/client';
import { ChevronDown, SearchCheck } from 'lucide-react';
import { usePathname } from 'next/navigation';
import { useCallback, useState } from 'react';
import { NavItem } from './components/navigation-item';
import { useRoutes } from './lib/useRoutes';
import { isPathActive } from './lib/utils';
import { useRef, useEffect } from 'react';

// Create a store to hold the refresh function
let routeRefreshHandler: (() => void) | null = null;

// Export the refresh function to be used elsewhere
export function refreshRoutes() {
  if (routeRefreshHandler) {
    routeRefreshHandler();
  }
}

function NavigationContent() {
  const pathname = usePathname();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { isLawyer } = useUser();
  const { baseRoutes, lawyerRoutes, userRoutes } = useRoutes();
  const [, forceUpdate] = useState({});
  const refreshRef = useRef(() => {});

  const allRoutes: Route[] = [
    ...baseRoutes,
    ...(!isLawyer ? userRoutes : lawyerRoutes),
  ];

  // Check if feature flags are bypassed
  const bypassFeatureFlags = false;

  // Filter based on feature flags
  const routes = allRoutes
    .filter((route) => {
      if (bypassFeatureFlags) {
        console.log(
          'Feature flags bypassed - route',
          route.title,
          'enabled by bypass'
        );
        return true;
      }
      if (!route.featureFlagKey) return true;
      const isEnabled = featureFlagsSelectors.isFeatureEnabled(
        route.featureFlagKey
      );
      console.log('Feature flag for route', route.title, 'is :', isEnabled);
      return isEnabled;
    })
    .map((route) => ({
      ...route,
      subRoutes: route.subRoutes?.filter((subRoute) => {
        if (bypassFeatureFlags) {
          console.log(
            'Feature flags bypassed - subRoute',
            subRoute.title,
            'enabled by bypass'
          );
          return true;
        }
        if (!subRoute.featureFlagKey) return true;
        const isEnabled = featureFlagsSelectors.isFeatureEnabled(
          subRoute.featureFlagKey
        );
        return isEnabled;
      }),
    }));

  // Find the active parent route
  const activeParentRoute = routes.find((route) =>
    isPathActive(pathname, route.path, route.subRoutes)
  );

  // Persist the latest forceUpdate callback
  useEffect(() => {
    refreshRef.current = () => forceUpdate({});
    routeRefreshHandler = refreshRef.current;

    return () => {
      // Clean up when component unmounts
      if (routeRefreshHandler === refreshRef.current) {
        routeRefreshHandler = null;
      }
    };
  }, []);

  // Store the refresh handler
  routeRefreshHandler = useCallback(() => {
    forceUpdate({});
  }, []);

  return (
    <nav className='relative' aria-label='Main navigation'>
      {/* Large screens (lg+): Show all items horizontally */}
      <div className='hidden items-center space-x-3 lg:flex'>
        {routes.map((route) => (
          <NavItem key={route.path} route={route} currentPathname={pathname} />
        ))}
      </div>

      {/* Medium screens (md-lg): Show only active parent with dropdown */}
      <div className='hidden md:block lg:hidden'>
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className={cn(
            'flex items-center gap-2 rounded-md px-3 pb-[6px] pt-2 text-sm font-medium',
            'text-primary hover:bg-gray-100 dark:hover:bg-gray-800'
          )}
        >
          {activeParentRoute && (
            <>
              <activeParentRoute.icon className='h-4 w-4' aria-hidden='true' />
              <span>{activeParentRoute.title}</span>
            </>
          )}
          <ChevronDown
            className={cn(
              'h-4 w-4 text-gray-500 transition-transform duration-200',
              isMenuOpen ? 'rotate-180' : ''
            )}
            aria-hidden='true'
          />
        </button>

        {isMenuOpen && (
          <div className='absolute left-1/2 top-full z-20 mt-2 w-64 -translate-x-1/2 rounded-lg border border-gray-200 bg-white p-2 shadow-lg dark:border-gray-700 dark:bg-gray-900'>
            {routes.map((route) => (
              <NavItem
                key={route.path}
                route={route}
                currentPathname={pathname}
                isCompact={true}
                onClick={() => setIsMenuOpen(false)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Small screens (sm and below): Only icons with dropdown */}
      <div className='md:hidden'>
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className={cn(
            'flex items-center gap-2 rounded-md px-3 pb-[6px] pt-2 text-sm font-medium',
            'text-primary hover:bg-gray-100 dark:hover:bg-gray-800'
          )}
        >
          {activeParentRoute ? (
            <activeParentRoute.icon className='h-6 w-6' aria-hidden='true' />
          ) : (
            <SearchCheck className='h-6 w-6' aria-hidden='true' />
          )}
          <ChevronDown
            className={cn(
              'h-4 w-4 text-gray-500 transition-transform duration-200',
              isMenuOpen ? 'rotate-180' : ''
            )}
            aria-hidden='true'
          />
        </button>

        {isMenuOpen && (
          <div className='absolute left-1/2 top-full z-20 mt-2 w-64 -translate-x-1/2 rounded-lg border border-gray-200 bg-white p-2 shadow-lg dark:border-gray-700 dark:bg-gray-900'>
            {routes.map((route) => (
              <NavItem
                key={route.path}
                route={route}
                currentPathname={pathname}
                isCompact={true}
                onClick={() => setIsMenuOpen(false)}
              />
            ))}
          </div>
        )}
      </div>
    </nav>
  );
}

export default function NavigationMenu() {
  return (
    <ClientI18nWrapper>
      <NavigationContent />
    </ClientI18nWrapper>
  );
}

function ClientI18nWrapper({ children }: { children: React.ReactNode }) {
  const locale = useCurrentLocale();
  return (
    <I18nProviderClient locale={locale} fallback='Loading...'>
      {children}
    </I18nProviderClient>
  );
}
